import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { deepCamelizeKeys } from "Utils/camelizeKeys";
import companyToken from "Utils/companyToken";
import { prepareHeadersWithCSRFToken } from "Utils/fetchWithCSRFToken";

import { appendAuthParams } from "Common/TokenAuth/Utils";

import { PlanSuiteCurrentCosts, PlanSuiteRenewalCosts, WithPlans } from "./planSuites.types";

type CurrentCostsArgs = {
  planSuiteID: string;
  includeFlags?: string[];
};

type CurrentCostsResponse = {
  planSuite: PlanSuiteCurrentCosts;
};

type RenewalCostsArgs = {
  planSuiteID: string;
  includeFlags?: string[];
};

type RenewalCostsResponse = {
  planSuite: PlanSuiteRenewalCosts;
};

export const planSuitesApi = createApi({
  reducerPath: "planSuites",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2/plan_suites",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),
  endpoints: (builder) => ({
    getPlanSuiteCurrentCosts: builder.query<CurrentCostsResponse, CurrentCostsArgs>({
      query: ({ planSuiteID, includeFlags }) => {
        // A planSuiteID of "current" is resolved from the company, which the backend
        // cannot pick for an account that administers more than one.
        const companyIDToken = companyToken();
        let data: Record<string, string> = companyIDToken ? { company_id_token: companyIDToken } : {};
        if (includeFlags?.length > 0) {
          data = { ...data, include: includeFlags.join(",") };
        }
        const params = new URLSearchParams(data).toString();
        return appendAuthParams(`/${planSuiteID}/current_costs?${params}`);
      },
      transformResponse: (response: CurrentCostsResponse, _meta, arg: CurrentCostsArgs) => {
        const { planSuite } = response;
        const { currentEmployeeCosts, currentPlanCosts, currentPlanSuiteCosts } = planSuite;
        const camilizedResponse = {
          planSuite: {
            ...planSuite,
            currentEmployeeCosts: deepCamelizeKeys(currentEmployeeCosts),
            currentPlanCosts: deepCamelizeKeys(currentPlanCosts),
            currentPlanSuiteCosts: deepCamelizeKeys(currentPlanSuiteCosts),
          },
        };
        if (arg.includeFlags?.includes("plans")) {
          return camilizedResponse as { planSuite: PlanSuiteCurrentCosts & WithPlans };
        }
        return camilizedResponse as { planSuite: PlanSuiteCurrentCosts };
      },
      providesTags: ["PlanSuiteWithCurrentCosts"],
    }),
    getPlanSuiteRenewalCosts: builder.query<RenewalCostsResponse, RenewalCostsArgs>({
      query: ({ planSuiteID, includeFlags }) => {
        let data = {};
        if (includeFlags?.length > 0) {
          data = { include: includeFlags.join(",") };
        }
        const params = new URLSearchParams(data).toString();
        return appendAuthParams(`/${planSuiteID}/renewal_costs?${params}`);
      },
      transformResponse: (response: RenewalCostsResponse, _meta, arg: RenewalCostsArgs) => {
        const { planSuite } = response;
        const { renewalEmployeeCosts, renewalPlanCosts, renewalPlanSuiteCosts } = planSuite;
        const camilizedResponse = {
          planSuite: {
            ...planSuite,
            renewalEmployeeCosts: deepCamelizeKeys(renewalEmployeeCosts),
            renewalPlanCosts: deepCamelizeKeys(renewalPlanCosts),
            renewalPlanSuiteCosts: deepCamelizeKeys(renewalPlanSuiteCosts),
          },
        };
        if (arg.includeFlags?.includes("plans")) {
          return camilizedResponse as { planSuite: PlanSuiteRenewalCosts & WithPlans };
        }
        return camilizedResponse as { planSuite: PlanSuiteRenewalCosts };
      },
      providesTags: ["PlanSuiteWithRenewalCosts"],
    }),
  }),
  tagTypes: ["PlanSuiteWithCurrentCosts", "PlanSuiteWithRenewalCosts"],
});

export const { useGetPlanSuiteCurrentCostsQuery, useGetPlanSuiteRenewalCostsQuery } = planSuitesApi;
